"use client";

/**
 * React Query bindings for the task canvas.
 *
 * `useTaskArtifacts` reads one task's own media and refetches whenever a job
 * settles (JOB_READY / JOB_ERROR) so the canvas tracks generation live, without
 * polling. `useSetKept` toggles the artifact "kept" flag. The artifact store is
 * the single source of truth; `id` is the public JOB id everywhere.
 */

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EVENTS } from "@bash/shared";
import { useAuth } from "@/contexts/AuthContext";
import { useConnectionStore } from "@/stores/connectionStore";
import { resilientFetch } from "@/lib/fetch";
import type { TaskArtifact } from "./lineage";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

interface TaskArtifactsResponse {
  artifacts: TaskArtifact[];
  next_before: string | null;
}

function taskArtifactsKey(taskId: string | null): readonly unknown[] {
  return ["task-artifacts", taskId] as const;
}

/**
 * Level-triggered convergence while renders are in flight: any artifact still
 * awaiting its thumbnail keeps the list on a gentle poll, so a ready thumb (or
 * a card whose registration emit was lost) lands within seconds even if no
 * socket event arrives. Push signals stay the fast path, never the only path.
 *
 * ONLY 'file' artifacts ride the PNG thumb lane (pdf/office posters), so only
 * they justify the poll. Media kinds render their own pixels and their rows sit
 * at thumb_status 'pending' FOREVER (the lane never runs for them) — polling on
 * those refetched every 8s, each refetch re-signed the url, and the shelf image
 * blinked on every fresh src (founder, 08-08).
 */
export function thumbsPendingRefetch(
  artifacts: TaskArtifact[] | undefined
): number | false {
  return artifacts?.some(
    (a) => a.thumb_status === "pending" && a.kind === "file"
  )
    ? 8_000
    : false;
}

async function fetchTaskArtifacts(
  taskId: string,
  token: string | null
): Promise<TaskArtifact[]> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await resilientFetch(
    `${SERVER_URL}/api/media/artifacts?task_id=${encodeURIComponent(
      taskId
    )}&limit=100`,
    { headers }
  );
  if (!res.ok) throw new Error(`task artifacts request failed: ${res.status}`);
  const body = (await res.json()) as TaskArtifactsResponse;
  return body.artifacts ?? [];
}

export interface UseTaskArtifacts {
  artifacts: TaskArtifact[];
  loading: boolean;
  error: unknown;
}

/**
 * Live artifacts for a single task (the canvas grid). Fetches the task's media
 * and refetches on every job settle event so ready/failed states land without
 * polling. Disabled (no fetch) when `taskId` is null.
 */
export function useTaskArtifacts(taskId: string | null): UseTaskArtifacts {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const getSocket = useConnectionStore((s) => s.getSocket);
  const isSocketConnected = useConnectionStore((s) => s.isSocketConnected);

  const query = useQuery<TaskArtifact[]>({
    queryKey: taskArtifactsKey(taskId),
    queryFn: async () => fetchTaskArtifacts(taskId!, await getAccessToken()),
    enabled: !!taskId,
    staleTime: 15_000,
    refetchInterval: (query) => thumbsPendingRefetch(query.state.data),
    // A 404 here means the feature flags are off, not a transient fault — the
    // default 3 retries would turn every flag-off panel open into 4 requests.
    retry: false,
  });

  useEffect(() => {
    if (!taskId || !isSocketConnected) return;
    const socket = getSocket();
    if (!socket) return;
    const refresh = () => {
      void queryClient.invalidateQueries({
        queryKey: taskArtifactsKey(taskId),
      });
    };
    socket.on(EVENTS.JOB_READY, refresh);
    socket.on(EVENTS.JOB_ERROR, refresh);
    // Presented deliverables (Pieces) land via present_output, not a media job,
    // so their own event refetches the list — the Pieces rail tracks handoffs live.
    socket.on(EVENTS.TASK_ARTIFACT_REGISTERED, refresh);
    return () => {
      socket.off(EVENTS.JOB_READY, refresh);
      socket.off(EVENTS.JOB_ERROR, refresh);
      socket.off(EVENTS.TASK_ARTIFACT_REGISTERED, refresh);
    };
  }, [taskId, isSocketConnected, getSocket, queryClient]);

  return {
    artifacts: query.data ?? [],
    loading: query.isPending && !!taskId,
    error: query.error,
  };
}

function userArtifactsKey(): readonly unknown[] {
  return ["user-artifacts"] as const;
}

async function fetchUserArtifacts(
  token: string | null
): Promise<TaskArtifact[]> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  // No task_id → the handler lists the caller's own artifacts across every
  // task (the Media Library read surface). 404 (both server flags off) → [].
  const res = await resilientFetch(
    `${SERVER_URL}/api/media/artifacts?limit=100`,
    { headers }
  );
  if (!res.ok) throw new Error(`user artifacts request failed: ${res.status}`);
  const body = (await res.json()) as TaskArtifactsResponse;
  return body.artifacts ?? [];
}

/**
 * Every artifact the user owns, across all tasks (the global Creations grid).
 * Refetches when a new deliverable registers so the folder tracks handoffs live.
 * `enabled: false` keeps the query fully dark (no fetch, no poll) for callers
 * behind their own flag — the home shelf reads it only when the shelf is on.
 */
export function useUserArtifacts(opts?: {
  enabled?: boolean;
}): UseTaskArtifacts {
  const enabled = opts?.enabled ?? true;
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const getSocket = useConnectionStore((s) => s.getSocket);
  const isSocketConnected = useConnectionStore((s) => s.isSocketConnected);

  const query = useQuery<TaskArtifact[]>({
    queryKey: userArtifactsKey(),
    queryFn: async () => fetchUserArtifacts(await getAccessToken()),
    enabled,
    staleTime: 15_000,
    refetchInterval: (query) =>
      enabled ? thumbsPendingRefetch(query.state.data) : false,
    retry: false,
  });

  useEffect(() => {
    if (!isSocketConnected) return;
    const socket = getSocket();
    if (!socket) return;
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: userArtifactsKey() });
    };
    socket.on(EVENTS.TASK_ARTIFACT_REGISTERED, refresh);
    return () => {
      socket.off(EVENTS.TASK_ARTIFACT_REGISTERED, refresh);
    };
  }, [isSocketConnected, getSocket, queryClient]);

  return {
    artifacts: query.data ?? [],
    loading: query.isPending,
    error: query.error,
  };
}

export interface SetKeptVars {
  id: string;
  kept: boolean;
}

type KeptSnapshot = Array<[readonly unknown[], TaskArtifact[] | undefined]>;

/**
 * Toggle the "kept" flag on an artifact (PATCH /api/media/artifacts/:id).
 *
 * Optimistic: the kept badge / Save→Remove toggle flip instantly across every
 * cached task-artifacts list, and roll back to the snapshot if the PATCH fails.
 */
export function useSetKept() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<void, Error, SetKeptVars, { snapshot: KeptSnapshot }>({
    mutationFn: async ({ id, kept }: SetKeptVars) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const token = await getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await resilientFetch(
        `${SERVER_URL}/api/media/artifacts/${encodeURIComponent(id)}`,
        { method: "PATCH", headers, body: JSON.stringify({ kept }) }
      );
      if (!res.ok) throw new Error(`set kept failed: ${res.status}`);
    },
    onMutate: async ({ id, kept }: SetKeptVars) => {
      await queryClient.cancelQueries({ queryKey: ["task-artifacts"] });
      const snapshot = queryClient.getQueriesData<TaskArtifact[]>({
        queryKey: ["task-artifacts"],
      });
      for (const [key, data] of snapshot) {
        if (!data) continue;
        queryClient.setQueryData<TaskArtifact[]>(
          key,
          data.map((a) => (a.id === id ? { ...a, kept } : a))
        );
      }
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["task-artifacts"] });
    },
  });
}

export interface SetDismissedVars {
  id: string;
  dismissed: boolean;
  /** Undo's restore: the row as it stood before the delete, put straight back
   *  into every list it belongs in (the refetch on settle confirms it). */
  artifact?: TaskArtifact;
}

/**
 * Every cached list that can show a creation: a task's own list
 * (`["task-artifacts", taskId]`), the user library behind the home Artifacts
 * tab and Knowledge's Creations folder (`["user-artifacts"]`), and the Media
 * library (`["media-artifacts", …]`, whose data is `{ artifacts, … }`).
 */
const CREATION_LISTS: ReadonlyArray<readonly unknown[]> = [
  ["task-artifacts"],
  ["user-artifacts"],
  ["media-artifacts"],
];

/** Delete and Undo share one key (the settle check below) and one scope, so
 *  an Undo's PATCH never overtakes the delete it undoes. */
const SET_DISMISSED_KEY = ["set-dismissed"] as const;

/** A list's cached data: an array, or the Media library's `{ artifacts, … }`. */
type CreationListData = TaskArtifact[] | { artifacts: TaskArtifact[] };

type DismissedSnapshot = Array<[readonly unknown[], unknown]>;

const newestFirst = (x: TaskArtifact, y: TaskArtifact): number =>
  x.created_at < y.created_at ? 1 : x.created_at > y.created_at ? -1 : 0;

/** Apply `edit` to a cached list, whichever of the two shapes it is in. */
function editList(
  data: unknown,
  edit: (list: TaskArtifact[]) => TaskArtifact[]
): unknown {
  if (Array.isArray(data)) return edit(data as TaskArtifact[]);
  const held = data as CreationListData | undefined;
  if (held && !Array.isArray(held) && Array.isArray(held.artifacts)) {
    return { ...held, artifacts: edit(held.artifacts) };
  }
  return data;
}

/** Whether the restored row belongs in this list: its own task's list, the
 *  user library, and the Media library while it is on. */
function belongsIn(
  key: readonly unknown[],
  data: unknown,
  a: TaskArtifact
): boolean {
  if (key[0] === "task-artifacts") return key[1] === a.task_id;
  if (key[0] === "media-artifacts")
    return (data as { available?: boolean }).available !== false;
  return key[0] === "user-artifacts";
}

/**
 * Delete a creation, or Undo that (PATCH /api/media/artifacts/:id with
 * `{ dismissed }`; the server's delete is the soft `dismissed_at`).
 *
 * Optimistic: a delete removes the row from EVERY cached list that shows
 * creations at once; an Undo carrying the row puts it back into each list it
 * belongs in, newest first. Either rolls back to the snapshot on failure. The
 * lists are refetched only once the LAST of these writes settles, so a refetch
 * started between a delete and its Undo never flickers the row out again.
 */
export function useSetDismissed() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    SetDismissedVars,
    { snapshot: DismissedSnapshot }
  >({
    mutationKey: SET_DISMISSED_KEY,
    scope: { id: SET_DISMISSED_KEY.join() },
    mutationFn: async ({ id, dismissed }: SetDismissedVars) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const token = await getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await resilientFetch(
        `${SERVER_URL}/api/media/artifacts/${encodeURIComponent(id)}`,
        { method: "PATCH", headers, body: JSON.stringify({ dismissed }) }
      );
      if (!res.ok) throw new Error(`set dismissed failed: ${res.status}`);
    },
    onMutate: async ({ id, dismissed, artifact }: SetDismissedVars) => {
      await Promise.all(
        CREATION_LISTS.map((queryKey) =>
          queryClient.cancelQueries({ queryKey })
        )
      );
      const snapshot: DismissedSnapshot = CREATION_LISTS.flatMap((queryKey) =>
        queryClient.getQueriesData({ queryKey })
      );
      for (const [key, data] of snapshot) {
        if (data === undefined) continue;
        if (dismissed) {
          queryClient.setQueryData(
            key,
            editList(data, (list) => list.filter((a) => a.id !== id))
          );
        } else if (artifact && belongsIn(key, data, artifact)) {
          queryClient.setQueryData(
            key,
            editList(data, (list) =>
              list.some((a) => a.id === artifact.id)
                ? list
                : [...list, artifact].sort(newestFirst)
            )
          );
        }
      }
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      // This write still counts itself; anything more is a delete or an Undo
      // in flight whose own settle will refetch.
      if (queryClient.isMutating({ mutationKey: SET_DISMISSED_KEY }) > 1)
        return;
      for (const queryKey of CREATION_LISTS) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
